import { VendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomyId } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import {
  RelatedLinkView,
  SeoContentView,
  StorefrontCardView,
  WeddingCardView,
} from '@zola/svc-marketplace-ts-types';

import type { PageCardView } from '~/pages/couples/explore/components/ExpertAdvice/ExpertAdvice';
import type { VendorSearchRequest } from '~/pages/couples/explore/components/SearchResults/types/types';
import type { FacetsByVendorType } from '~/reducers/ui/couplesSearch';
import { ClientApiServiceType } from '~/util/apiService';

import { ServerApiServiceType } from '../server/util/apiService';
import { getExpertAdviceArticlesForVendorSearch } from './expertAdvice';
import { getRelatedLinksForVendorSearch, getSeoContentBlockHtml } from './seo';
import { determineMarketSupplyForVendorSearch } from './vendorMarket';
import { getRealWeddingForVendorSearch } from './weddings';

export const getAdjacentVendorsForVendorSearch = (
  vendorSearch: VendorSearchRequest,
  apiService: ServerApiServiceType | ClientApiServiceType
): Promise<StorefrontCardView[]> => {
  const { selectedLocation, selectedTaxonomy } = vendorSearch;
  const vendorMarketId = selectedLocation?.storefrontSearchPayload?.vendorMarketId || undefined;
  const taxonomyKey = selectedTaxonomy?.key;
  return getAdjacentVendors(
    {
      vendorMarketId,
      taxonomyKey,
    },
    apiService
  );
};

export const getAdjacentVendors = (
  {
    vendorMarketId,
    taxonomyKey,
    limit = 12,
    offset = 0,
  }: {
    vendorMarketId: number | undefined;
    taxonomyKey: VendorTaxonomyKey | undefined;
    limit?: number;
    offset?: number;
  },
  apiService: ServerApiServiceType | ClientApiServiceType
): Promise<StorefrontCardView[]> => {
  if (vendorMarketId && taxonomyKey) {
    const taxonomyId = getVendorTaxonomyId(taxonomyKey);
    return apiService
      .get<StorefrontCardView[]>(
        `/web-marketplace-api/v3/storefront/adjacent-markets?vendor_market_id=${vendorMarketId}&taxonomy_node_id=${taxonomyId}&offset=${offset}&limit=${limit}`
      )
      .then((result) => {
        if (result && result.length > 0) {
          return result;
        }
        return [];
      })
      .catch(() => {
        return [];
      });
  } else {
    return Promise.resolve([]);
  }
};

/**
 * Fetches data for the search results page for the current search, in phase one
 * of SSR conversion, this is all the category and location dependent data such as
 * is the market supplied with vendors and all the information needed for SEO and
 * other content on the page.  In phase two, this may execute the search as well.
 *
 * @param vendorSearch
 * @param facetsByVendorType
 * @param apiService
 * @returns
 */
export const fetchVendorSearchResultsPageData = (
  vendorSearch: VendorSearchRequest,
  facetsByVendorType: FacetsByVendorType,
  apiService: ServerApiServiceType | ClientApiServiceType
): Promise<
  [
    boolean,
    StorefrontCardView[],
    RelatedLinkView[],
    WeddingCardView[],
    PageCardView[],
    SeoContentView | null
  ]
> => {
  return Promise.all([
    determineMarketSupplyForVendorSearch(vendorSearch, apiService),
    getAdjacentVendorsForVendorSearch(vendorSearch, apiService),
    getRelatedLinksForVendorSearch(vendorSearch, apiService),
    getRealWeddingForVendorSearch(vendorSearch, apiService),
    getExpertAdviceArticlesForVendorSearch(vendorSearch, apiService),
    getSeoContentBlockHtml(vendorSearch, apiService),
  ]);
};
